import React, { FC, SyntheticEvent, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { Box, MenuItem, TextField, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { enhancedMerchantAdminApi, MerchantSummaryDto } from '@services';
import ModalDialogComponent from '../ModalDialogComponent/ModalDialogComponent';
import {
    selectAllAvailableMerchants,
    selectCanSwitchMerchant,
    selectCurrentMerchant,
    switchMerchant,
} from '../../auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import ListItemLink from '../ListItemLink/ListItemLink';
import { MerchantsIcon } from '../IconSelectorComponent/IconSelectorComponent';

export interface SwitchMerchantDialogComponentProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onCancel: () => void;
}

export const SwitchMerchantDialogComponent: FC<SwitchMerchantDialogComponentProps> = (props) => {
    const { open, setOpen, onCancel } = props;
    const [confirmEnabled, setConfirmEnabled] = useState<boolean>(false);
    const [selectedMerchant, setSelectedMerchant] = useState<MerchantSummaryDto | null>(null);
    const [merchantListOpen, setMerchantListOpen] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const merchants = useAppSelector(selectAllAvailableMerchants);

    const handleInputChange = (event: SyntheticEvent, value?: string) => {
        const merchantNames = merchants.map((merchant) => merchant.name);
        if (value !== null && merchantNames.includes(value!)) {
            setConfirmEnabled(true);
            setSelectedMerchant(merchants.find((item) => item.name === value)!);
        } else {
            setConfirmEnabled(false);
            setSelectedMerchant(null);
        }
    };

    const handleSwitchMerchant = () => {
        dispatch(switchMerchant({ merchant: selectedMerchant! }));
        dispatch(enhancedMerchantAdminApi.util.invalidateTags(['UserContext']));
        setOpen(false);
        navigate('/');
    };

    return (
        <ModalDialogComponent
            data-testid="SwitchMerchantDialogComponent"
            title="Switch merchant"
            confirmText="Switch"
            open={open}
            setOpen={setOpen}
            onConfirm={() => handleSwitchMerchant()}
            confirmEnabled={confirmEnabled}
            onCancel={onCancel}
        >
            <Autocomplete
                freeSolo
                id="search-merchant-name"
                disableClearable
                fullWidth
                open={merchantListOpen}
                onOpen={() => {
                    setMerchantListOpen(true);
                }}
                onClose={() => {
                    setMerchantListOpen(false);
                }}
                options={merchants.map((merchant) => merchant.name)}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                    <TextField
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...params}
                        sx={{ my: 2, mx: 0 }}
                        label="Search merchant name"
                        InputProps={{
                            ...params.InputProps,
                            type: 'search',
                        }}
                    />
                )}
            />
        </ModalDialogComponent>
    );
};

interface SwitchMerchantMenuItemProps {
    closeMenu: () => void;
}

const SwitchMerchantMenuItem: FC<SwitchMerchantMenuItemProps> = (props) => {
    const { closeMenu } = props;
    const theme = useTheme();
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
    const canSwitch = useAppSelector(selectCanSwitchMerchant);
    const currentMerchant = useAppSelector(selectCurrentMerchant);

    const switchMerchantLabel = currentMerchant?.name
        ? `Merchant: ${currentMerchant.name.length > 10 ? `${currentMerchant.name.substring(0, 10)}...` : currentMerchant.name}`
        : 'Switch Merchant';

    if (!canSwitch) {
        return null;
    }

    const handleSwitchMenuClick = () => {
        setShowDialog(true);
    };

    return (
        <div data-testid="SwitchMerchantMenuItem">
            {isMobileLayout ? (
                <Box>
                    <ListItemLink
                        icon={<MerchantsIcon />}
                        text={switchMerchantLabel}
                        onClick={handleSwitchMenuClick}
                    />
                </Box>
            ) : (
                <Box>
                    <MenuItem onClick={handleSwitchMenuClick}>
                        {switchMerchantLabel}
                    </MenuItem>
                </Box>
            )}

            <SwitchMerchantDialogComponent
                open={showDialog}
                setOpen={setShowDialog}
                onCancel={closeMenu}
            />
        </div>
    );
};

export default SwitchMerchantMenuItem;
